import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { PlusIcon } from "lucide-react";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { AddDocumentDrawerRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/AddDocumentDrawerRedesign";
import { DocumentListRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentListRedesign";
import { PortfolioListRedesign } from "web_myplann/Portfolio/Components/Redesign/PortfolioListRedesign";
import { usePortfolioTabRouting } from "web_myplann/Portfolio/Hooks/usePortfolioTabRouting";
import {
	PORTFOLIO_TABS,
	PortfolioTab,
} from "web_myplann/Portfolio/Types/portfolioTabs";
import {
	NativePullToRefresh,
	PULL_TO_REFRESH_SPINNER_SIZE,
} from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannTabsRedesign } from "web_myplann/Shared/Components/Redesign/MyPlannTabsRedesign";
import { MyPlannTopBarRedesign } from "web_myplann/Shared/Components/Redesign/MyPlannTopBarRedesign";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";

type Props = {
	hasPortfolioRight: boolean;
};

export const PortfolioPageRedesign: FC<Props> = ({ hasPortfolioRight }) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { value, onChange: handleChange } = usePortfolioTabRouting();
	const [documentDrawer, setDocumentDrawer] = useState(false);

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.grey[50]}
			topBarBgColor={theme.palette.background.paper}
			topBar={
				<Stack
					sx={{
						width: "100%",
						bgcolor: theme.palette.background.paper,
					}}
				>
					<MyPlannTopBarRedesign
						title={t(PORTFOLIO_TABS[value].labelKey)}
						endComponent={
							value === PortfolioTab.Portfolio ? (
								hasPortfolioRight && (
									<IconButton
										onClick={() =>
											navigate(Routing.Portfolio.PortfolioAddItem.path())
										}
									>
										<PlusIcon
											size={theme.iconSize.md}
											color={theme.palette.grey[600]}
										/>
									</IconButton>
								)
							) : (
								<IconButton onClick={() => setDocumentDrawer(true)}>
									<PlusIcon
										size={theme.iconSize.md}
										color={theme.palette.grey[600]}
									/>
								</IconButton>
							)
						}
					/>
					<Box
						pt={theme.spacingTokens.sm}
						px={theme.spacingTokens.lg}
						pb={theme.spacingTokens.md}
					>
						<MyPlannTabsRedesign
							value={value}
							onChange={handleChange}
							tabs={PORTFOLIO_TABS.map((tab) => ({
								id: tab.id,
								label: t(tab.labelKey),
							}))}
						/>
					</Box>
				</Stack>
			}
		>
			<NativePullToRefresh
				queryKeys={
					value === PortfolioTab.Portfolio
						? [
								PortfolioKeys.portfolioLists(),
								PortfolioKeys.propertyLists(),
								PortfolioKeys.reportingSummary(),
							]
						: [DocumentKeys.allLists]
				}
				spinnerOffset={-PULL_TO_REFRESH_SPINNER_SIZE - 20}
			>
				<Box hidden={value !== PortfolioTab.Portfolio}>
					{value === PortfolioTab.Portfolio && (
						<PortfolioListRedesign hasPortfolioRight={hasPortfolioRight} />
					)}
				</Box>
				<Box hidden={value !== PortfolioTab.Documents}>
					{value === PortfolioTab.Documents && (
						<DocumentListRedesign
							onAddDocument={() => setDocumentDrawer(true)}
						/>
					)}
				</Box>
			</NativePullToRefresh>

			<AddDocumentDrawerRedesign
				open={documentDrawer}
				onClose={() => setDocumentDrawer(false)}
			/>
		</MyPlannLayout>
	);
};
